<template>
  <dropdown
    ref="filterDropdown"
    :force-visibility="showDropdown"
    title="Choose a new filter..."
    popper-min-width-size="large"
    class="breadcrumb-filter-picker marker--breadcrumb-filter-picker"
  >
    <div class="label">
      <span :class="{'deemphasized' : deemphasized}" class="title">{{currentFilter.label}}</span>
      <span v-if="totalItemCount !== null" class="item-count">{{totalItemCount}}</span>
      <i class="ddi ddi-caret-down" />
    </div>
    <template #content>
      <div>
        <loading-placeholder v-if="!filtersLoaded" :lines="6" />
        <h5>Filters</h5>
        <template v-if="hasCustomFilters">
          <ul class="nav-list">
            <li
              v-for="(customFilter, index) in internalCustomFilters"
              :key="index"
              :class="{'selected': currentFilter.value === customFilter.value}"
              @click="handleFilterSelect(customFilter.value)"
            >
              <span>{{ customFilter.label }}</span>
              <i
                v-if="allowFilterDelete"
                class="ddi ddi-trash"
                @click.stop="handleCustomFilterDelete(customFilter.value)"
              />
            </li>
          </ul>
          <hr>
        </template>
        <ul class="nav-list">
          <li
            v-for="(defaultFilter, index) in internalDefaultFilters"
            :key="index"
            :class="[{'indented': defaultFilter.indented}, {'selected': currentFilter.value === defaultFilter.value}]"
            @click="handleFilterSelect(defaultFilter.value)"
          >
            <span>
              <i :class="`ddi-${defaultFilter.icon}`" class="ddi" />
              <strong v-if="!defaultFilter.indented">{{ defaultFilter.label }}</strong>
              <template v-else>{{ defaultFilter.label }}</template>
            </span>
            <div class="count">{{ getFilterCount(defaultFilter)}}</div>
          </li>
        </ul>
        <template v-if="hasActiveAssigneeCounts">
          <hr>
          <h5>Active by Assignee</h5>
          <ul class="nav-list">
            <li
              v-for="assignee in activeAssigneeCounts"
              :key="assignee.id"
              :class="['indented', {'selected': currentFilter.value === getAssigneeFilterIdentifier(assignee.id)}]"
              @click="handleAssigneeSelect(assignee.id)"
            >
              <span>{{ assignee.name }}</span>
              <div class="count">{{ assignee.count }}</div>
            </li>
          </ul>
        </template>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent, PropType } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
/* Data types */
import { INavMenuItem } from '@/types/data-objects/global'

/* API */

export default defineComponent({
  name: 'BreadcrumbFilterPicker',
  props: {
    deemphasized: {
      type: Boolean,
      default: false,
    },
    defaultFilters: {
      type: Array as () => INavMenuItem[],
      default: null,
    },
    customFilters: {
      type: Array as () => INavMenuItem[],
      default: null,
    },
    currentFilter: {
      type: Object as () => INavMenuItem,
      required: true,
    },
    totalItemCount: {
      type: Number,
      default: null,
    },
    allowFilterDelete: {
      type: Boolean,
      default: false,
    },
    activeAssigneeCounts: {
      type: Array as PropType<{ id: number; name: string; count: number }[]>,
      default: (): any[] => [],
    },
  },

  emits: ['change-filter', 'delete-custom-filter', 'select-assignee'],

  data() {
    return {
      filtersLoaded: false,
      showDropdown: false,
      lazyLoadedDefaultFilters: [] as INavMenuItem[],
      lazyLoadedCustomFilters: [] as INavMenuItem[],
    }
  },

  computed: {
    hasCustomFilters(): boolean {
      return this.customFilters.length > 0
    },
    hasActiveAssigneeCounts(): boolean {
      return this.activeAssigneeCounts && this.activeAssigneeCounts.length > 0
    },
    internalDefaultFilters(): INavMenuItem[] {
      if (this.defaultFilters) {
        return this.defaultFilters
      }
      if (this.lazyLoadedDefaultFilters.length > 0) {
        return this.lazyLoadedDefaultFilters
      }
      return [] as INavMenuItem[]
    },
    internalCustomFilters(): INavMenuItem[] {
      if (this.customFilters) {
        return this.customFilters
      }
      if (this.lazyLoadedCustomFilters.length > 0) {
        return this.lazyLoadedCustomFilters
      }
      return [] as INavMenuItem[]
    },
  },

  watch: {
    defaultFilters: {
      deep: 1,
      immediate: true,
      handler(val) {
        this.filtersLoaded = val
      },
    },
  },

  methods: {
    getFilterCount(item: INavMenuItem) {
      if (item.count === null) {
        return ''
      }

      if (item.count > 999) {
        return '999+'
      }

      if (item.count === 0) {
        return ''
      }

      return item.count
    },
    getAssigneeFilterIdentifier(val: number): string {
      return ItemFilterAssistant.getAssigneeFilterIdentifier(val)
    },
    handleFilterSelect(value: string) {
      this.$emit('change-filter', value)
      // @ts-ignore
      this.$refs.filterDropdown.handleClose()
    },
    handleCustomFilterDelete(value: string) {
      this.$emit('delete-custom-filter', value)
    },
    handleAssigneeSelect(assigneeId: number) {
      this.$emit('select-assignee', assigneeId)
      // @ts-ignore
      this.$refs.filterDropdown.handleClose()
    },
  },
})
</script>

<style lang="scss">
.breadcrumb-filter-picker {
  @include single-line-ellipsis;
  max-width: 100%;

  &:hover {
    .title {
      border-bottom: $border--solid-medium;
    }
  }

  .title.deemphasized {
    color: var(--color-text-primary);
  }

  .label {
    @include single-line-ellipsis;
    color: var(--color-text-secondary);

    .item-count {
      padding-left: 8px;
      color: var(--color-text-third);
    }

    .ddi-caret-down {
      margin-left: 6px;
      font-size: 10px;
      top: -1px;
    }
  }

  /* This dropdown usurps the default styles of the <dropdown> popper / action-sheet classes */
  .dropdown__popper {
    max-width: 200px;

    ul.nav-list {
      > li {
        &.indented {
          padding-left: 12px;
          &.selected {
            padding-left: 11px;
          }
        }

        &.selected {
          padding: 5px 4px;
          background-color: var(--color-background-blue-15);
          border: dotted 1px $color--blue;
        }

        > .ddi-trash {
          margin-left: auto;
          font-size: 14px;
          @include clickable-gray-color-states;
        }

        > div.count {
          margin-left: auto;
          color: var(--color-text-third);
        }
      }
    }
  }

  .action-sheet {
    .action-sheet-body {
      margin-bottom: 32px;

      > div {
        ul.nav-list {
          > li {
            &.indented {
              padding-left: 12px;
              &.selected {
                padding-left: 11px;
              }
            }

            &.selected {
              padding: 5px 4px;
              background-color: var(--color-background-blue-15);
              border: dotted 1px $color--blue;
            }

            > .ddi-trash {
              margin-left: auto;
              color: var(--color-text-third);
              font-size: 14px;
              &:hover {
                color: var(--color-text-primary);
              }
            }

            > div.count {
              margin-left: auto;
              color: var(--color-text-third);
            }
          }
        }
      }
    }
  }
}
</style>
